import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {Flame, Clock} from 'lucide-react';

// מיפוי אזורי זמן לערים מרכזיות
const TIMEZONE_TO_CITY = {
    // ישראל ומזרח אירופה
    'Asia/Jerusalem': {geonameid: '281184', offset: 40},
    'Israel': {geonameid: '281184', offset: 40},
    'Asia/Tel_Aviv': {geonameid: '293397', offset: 18},
    'Europe/Kiev': {geonameid: '293397', offset: 18},

    // אירופה
    'Europe/London': {geonameid: '2643743', offset: 18},
    'Europe/Paris': {geonameid: '2988507', offset: 18},
    'Europe/Berlin': {geonameid: '2950159', offset: 18},

    // צפון אמריקה
    'America/New_York': {geonameid: '5128581', offset: 18},
    'America/Chicago': {geonameid: '4887398', offset: 18},
    'America/Los_Angeles': {geonameid: '5368361', offset: 18},

    // אוסטרליה ואסיה
    'Australia/Sydney': {geonameid: '2147714', offset: 18},
    'Asia/Tokyo': {geonameid: '1850147', offset: 18},
    'Asia/Shanghai': {geonameid: '1796236', offset: 18}
};

const HolidayClosure = () => {
    const { t } = useTranslation();
    const [closureState, setClosureState] = useState({
        isClosed: false,
        reason: 'שבת'
    });

    // פונקציה למציאת העיר המתאימה לפי אזור זמן
    const getCityByTimezone = () => {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return TIMEZONE_TO_CITY[timezone] || TIMEZONE_TO_CITY['Asia/Jerusalem']; // ברירת מחדל לירושלים
        } catch (error) {
            console.warn('Error detecting timezone:', error);
            return TIMEZONE_TO_CITY['Asia/Jerusalem'];
        }
    };

    // פונקציה למציאת העיר הקרובה ביותר לפי קואורדינטות
    const findNearestCity = (lat, lon) => {
        // רשימת ערים עם קואורדינטות
        const MAJOR_CITIES = {
            'Jerusalem': {lat: 31.7683, lon: 35.2137, geonameid: '281184', offset: 40},
            'Tel Aviv': {lat: 32.0853, lon: 34.7818, geonameid: '293397', offset: 18},
            'New York': {lat: 40.7128, lon: -74.0060, geonameid: '5128581', offset: 18},
            // אפשר להוסיף עוד ערים
        };

        let nearestCity = 'Jerusalem';
        let minDistance = Infinity;

        Object.entries(MAJOR_CITIES).forEach(([city, coords]) => {
            const distance = Math.sqrt(
                Math.pow(lat - coords.lat, 2) +
                Math.pow(lon - coords.lon, 2)
            );
            if (distance < minDistance) {
                minDistance = distance;
                nearestCity = city;
            }
        });

        return MAJOR_CITIES[nearestCity];
    };

    useEffect(() => {
        const fetchShabbatTimes = async () => {
            try {
                let cityData = getCityByTimezone(); // קבלת נתוני עיר לפי אזור זמן

                // נסיון לקבל מיקום מדויק
                if ("geolocation" in navigator) {
                    try {
                        const position = await new Promise((resolve, reject) => {
                            navigator.geolocation.getCurrentPosition(resolve, reject, {
                                timeout: 5000,
                                maximumAge: 300000
                            });
                        });

                        // מציאת העיר הקרובה ביותר למיקום המשתמש
                        const nearestCity = findNearestCity(
                            position.coords.latitude,
                            position.coords.longitude
                        );
                        cityData = nearestCity;
                    } catch (error) {
                        console.warn('Using timezone-based location');
                    }
                }

                const today = new Date();
                const url = `https://www.hebcal.com/shabbat?cfg=json&geo=geoname&geonameid=${cityData.geonameid}&M=on&b=${cityData.offset}`;

                const response = await fetch(url);
                const data = await response.json();

                if (!data.items) throw new Error('No Shabbat times found');

                const candleLighting = data.items.find(item => item.category === "candles")?.date;
                const havdalah = data.items.find(item => item.category === "havdalah")?.date;

                if (!candleLighting || !havdalah) throw new Error('Invalid Shabbat times');

                const now = new Date();
                const candleTime = new Date(candleLighting);
                const havdalahTime = new Date(havdalah);

                // בדיקת תקינות הזמנים
                const timeDiffHours = (havdalahTime - candleTime) / (1000 * 60 * 60);
                if (timeDiffHours < 20 || timeDiffHours > 26) {
                    throw new Error('Invalid time difference between candles and havdalah');
                }

                // הוספת באפר בטיחות של 5 דקות
                const SAFETY_BUFFER = 5 * 60 * 1000;
                const isShabbat = (
                    now >= new Date(candleTime.getTime() - SAFETY_BUFFER) &&
                    now <= new Date(havdalahTime.getTime() + SAFETY_BUFFER)
                );

                setClosureState({
                    isClosed: isShabbat,
                    reason: 'שבת'
                });

                // שמירת הזמנים למקרה של כשל בבדיקה הבאה
                localStorage.setItem('lastValidShabbatTimes', JSON.stringify({
                    candleLighting,
                    havdalah,
                    timestamp: new Date().toISOString(),
                    cityData
                }));

            } catch (error) {
                console.error('Error fetching Shabbat times:', error);

                // שימוש בזמנים השמורים במקרה של כשל
                try {
                    const saved = localStorage.getItem('lastValidShabbatTimes');
                    if (saved) {
                        const {candleLighting, havdalah, timestamp} = JSON.parse(saved);
                        const age = new Date() - new Date(timestamp);

                        // שימוש בזמנים שמורים רק אם הם מהיממה האחרונה
                        if (age < 24 * 60 * 60 * 1000) {
                            const now = new Date();
                            const isShabbat = (
                                now >= new Date(candleLighting) &&
                                now <= new Date(havdalah)
                            );
                            setClosureState({
                                isClosed: isShabbat,
                                reason: 'שבת'
                            });
                        }
                    }
                } catch (storageError) {
                    console.error('Error reading saved times:', storageError);
                }
            }
        };

        fetchShabbatTimes();
        const interval = setInterval(fetchShabbatTimes, 60000); // בדיקה כל דקה

        return () => clearInterval(interval);
    }, []);

    if (!closureState.isClosed) return null;

    return (
        <div className="fixed inset-0 z-[999] bg-gradient-to-b from-[#d8c7aa]/98 to-[#ece4d1]/98 backdrop-blur-md">
            <div className="min-h-screen flex flex-col items-center justify-center p-6">
                <div
                    className="w-full max-w-2xl mx-auto bg-white/90 rounded-3xl shadow-2xl p-8 space-y-8 border-2 border-[#D4B797]">
                    <h1 className="text-5xl md:text-6xl font-bold text-[#5C4033] text-center font-heebo mb-6">
                        {t('shabatClosure.shabat')}
                    </h1>

                    <div className="flex justify-center space-x-8 rtl:space-x-reverse">
                        <Flame className="w-14 h-14 text-[#8B5E3C]"/>
                    </div>

                    <div className="space-y-4 text-center">
                        <p className="text-2xl font-heebo text-[#5C4033] font-bold">
                            {t('shabatClosure.title')}
                        </p>
                        <p className="text-xl font-heebo text-[#8B5E3C]">
                            {t('shabatClosure.backToActivity')} {t('shabatClosure.afterShabat')}
                        </p>
                    </div>

                    <div className="flex justify-center mt-8">
                        <Clock className="w-6 h-6 text-[#8B5E3C]"/>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HolidayClosure;